import {
  Add as AddIcon,
  Close as CloseIcon,
  OpenInFull as OpenFullIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Slide,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router";
import { useArgusInstances, useArgusSessions, useAutoResumeArgusInstance } from "../../hooks/argus";
import { stripContextPrefix } from "../../lib/argus/argus-text";
import { useArgusConnectionContext } from "../../providers/argus-connection-provider";
import { SIDEBAR } from "../../theme/theme";
import { DespleaIcon } from "../icons/desplega-icon";
import { ArgusContextPill } from "./argus-context-pill";
import { ArgusMessageInput } from "./argus-message-input";
import { ArgusMessageList } from "./argus-message-list";

/**
 * Sliding right-hand Argus panel.
 *
 * Pencil: Frame 2 (`o9eBQ`).
 *
 * Mounted as sibling to `<Layout />` in App.tsx. Consumes the same
 * `ArgusConnectionProvider` as the full `/argus` page so messages, events,
 * and session state are shared — navigating to `/argus` from the sidebar
 * shows the exact same conversation.
 *
 * Route-aware: hidden on `/argus` (the full page is that experience), and
 * hidden when the user has no instance yet (direct them to `/argus` via a
 * button from within the sidebar in a future polish pass — Phase 6).
 */

export const ARGUS_SIDEBAR_WIDTH = 400;

export const ArgusSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();
  const {
    sidebarOpen,
    setSidebarOpen,
    setActiveSessionId,
    events,
    seededMessages,
    hydratedOpenToolCallIds,
    isConnected,
    instanceStatus,
    sendMessage,
    stopSession,
    activeSessionId,
    isAgentWorking,
    wasStopped,
    queuedMessages,
    clearQueue,
    removeFromQueue,
    promoteToSteer,
  } = useArgusConnectionContext();

  // Sessions for the selector
  const { instances } = useArgusInstances({ limit: 1 });
  const instanceId = instances?.[0]?.id;
  const { sessions } = useArgusSessions(instanceId);

  // Skipped on /argus because the full page owns auto-resume there.
  const autoResume = useAutoResumeArgusInstance({
    instanceId,
    instanceStatus,
    enabled: !location.pathname.startsWith("/argus"),
  });

  // Hide on /argus — the full page is the Argus experience there.
  if (location.pathname.startsWith("/argus")) return null;

  // On mobile we switch to a full-height temporary MUI Drawer instead of
  // the pushing panel. On desktop the Slide-in panel below remains.
  if (!sidebarOpen && !isMobile) return null;

  const canChat = instanceStatus === "active";
  const placeholder = autoResume.isAutoResuming
    ? "Reconnecting…"
    : !canChat
      ? instanceStatus === "paused"
        ? "Resume your Argus instance to chat"
        : instanceStatus === "booting"
          ? "Argus is booting…"
          : "Argus is not ready yet"
      : activeSessionId
        ? "Ask Argus about this page…"
        : "Start a new session";

  const handleSend = async (message: string, method?: "steer" | "followUp") => {
    try {
      await sendMessage(message, method);
    } catch (err) {
      enqueueSnackbar(`Send failed: ${err instanceof Error ? err.message : "unknown error"}`, {
        variant: "error",
      });
    }
  };

  const handleStop = async () => {
    try {
      await stopSession();
    } catch (err) {
      enqueueSnackbar(`Stop failed: ${err instanceof Error ? err.message : "unknown error"}`, {
        variant: "error",
      });
    }
  };

  const body = (
    <>
      <Stack sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        {/* Title row */}
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ paddingX: 1.5, paddingTop: 1.5, paddingBottom: 0.75 }}
        >
          <DespleaIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 0 }}>
            Argus
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ArgusContextPill />
          </Box>
          <Tooltip title="Open full page">
            <IconButton
              size="small"
              aria-label="Open Argus full page"
              onClick={() => navigate("/argus")}
            >
              <OpenFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close sidebar">
            <IconButton
              size="small"
              aria-label="Close Argus sidebar"
              onClick={() => setSidebarOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        {/* Session selector */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ paddingX: 1.5, paddingBottom: 1 }}
        >
          <Select
            value={activeSessionId ?? ""}
            onChange={(e) => {
              const val = e.target.value as string;
              if (!val) return;
              // Match the /argus page's session-select behavior: picking a
              // session from the sidebar jumps to the full /argus page with
              // the session pre-selected via `?session=<agent_session_id>`.
              // Without this the URL stays on the current non-/argus page
              // while the sidebar chat silently switches sessions — users
              // reported the row "not navigating" (F25).
              setActiveSessionId(val);
              navigate(`/argus?session=${encodeURIComponent(val)}`);
            }}
            displayEmpty
            size="small"
            sx={{
              flex: 1,
              fontSize: "12px",
              "& .MuiSelect-select": { paddingY: "4px", paddingX: "8px" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
            }}
            renderValue={(value) => {
              if (!value) return <em style={{ opacity: 0.5 }}>New session</em>;
              const session = sessions?.find((s) => (s.agent_session_id ?? s.id) === value);
              const label = stripContextPrefix(session?.first_message) || "Session";
              return label.length > 40 ? `${label.slice(0, 40)}…` : label;
            }}
          >
            {(sessions ?? []).map((s) => {
              const gid = s.agent_session_id ?? s.id;
              const label = stripContextPrefix(s.first_message) || "Untitled";
              return (
                <MenuItem key={s.id} value={gid} sx={{ fontSize: "12px" }}>
                  {label.length > 50 ? `${label.slice(0, 50)}…` : label}
                </MenuItem>
              );
            })}
          </Select>
          <Tooltip title="New session">
            <IconButton
              size="small"
              aria-label="New session"
              onClick={() => setActiveSessionId(null)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {autoResume.isAutoResuming && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <CircularProgress size={14} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Reconnecting…
          </Typography>
        </Stack>
      )}

      {autoResume.hasReachedLimit && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Typography variant="caption" sx={{ color: "error.main" }}>
            Couldn't reconnect
          </Typography>
          <Button size="small" aria-label="Retry reconnecting to Argus" onClick={autoResume.retry}>
            Retry
          </Button>
        </Stack>
      )}

      <ArgusMessageList
        events={events}
        seededMessages={seededMessages}
        isConnected={isConnected}
        hydratedOpenToolCallIds={hydratedOpenToolCallIds}
      />

      <ArgusMessageInput
        disabled={!canChat}
        placeholder={placeholder}
        onSend={handleSend}
        isAgentWorking={isAgentWorking}
        wasStopped={wasStopped}
        queuedMessages={queuedMessages}
        onRemoveFromQueue={removeFromQueue}
        onPromoteToSteer={promoteToSteer}
        onClearQueue={clearQueue}
        onStop={handleStop}
      />
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Slide direction="left" in mountOnEnter unmountOnExit>
      <Box
        component="aside"
        aria-label="Argus sidebar"
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: `${ARGUS_SIDEBAR_WIDTH}px`,
          backgroundColor: "background.paper",
          borderLeft: "1px solid",
          borderColor: SIDEBAR.divider,
          boxShadow: "-4px 0 12px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          zIndex: 1150,
        }}
      >
        {body}
      </Box>
    </Slide>
  );
};
